<template>
  <v-dialog
    v-model="vehicleBulkUploadDialog"
    persistent
    scrollable
    width="90%"
    max-width="1200px"
  >
    <v-card class="pa-4">
      <v-card-title class="d-flex justify-space-between">
        <span
          class="text-lg-subtitle-1 text-xl-h6 text-uppercase font-weight-black primary--text"
        >
          {{ `${getTextChange(uploadTo)}` }}
        </span>
        <v-btn depressed fab class="mt-n3" small @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="pt-8">
          <v-col v-if="uploadTo == 'bulk_da_upload'" cols="4" class="pr-2">
            <MultiSelect
              label="Select Customer *"
              :items="customerList"
              itemText="company_title"
              itemValue="id"
              :loading="customerLoading"
              v-model="selectedCustomer"
              hide-details="auto"
              clearable
            />
          </v-col>

          <v-col :cols="uploadTo == 'bulk_da_upload' ? 4 : 5" class="d-flex">
            <v-file-input
              v-model="file"
              hide-details
              dense
              :label="`${'upload document'}`"
              prepend-inner-icon="mdi-attachment mdi-rotate-90"
              class="mr-4"
              prepend-icon=""
              outlined
              @change="getFileData($event)"
              accept=".xlsx, .xls, .csv"
            />
            <v-icon color="primary" @click="openInstructionDialog(uploadTo)">
              mdi-information
            </v-icon>
          </v-col>
          <v-col :cols="uploadTo == 'bulk_da_upload' ? 4 : 7" class="d-flex justify-end text-capitalize">
            <v-btn
              v-if="
                uploadTo == 'items_update' ||
                uploadTo == 'drivers_update' ||
                uploadTo == 'vehicles_update' ||
                uploadTo == 'customers_update' ||
                uploadTo == 'lane_rates_update' ||
                uploadTo == 'location_update' ||
                uploadTo == 'driver_bonus_update'
              "
              class="primary"
              depressed
              @click="downloadSampleExcel()"
            >
              {{ "download sample excel file" }}
            </v-btn>
            <v-btn
              v-else-if="uploadTo == 'route'"
              class="primary"
              depressed
              @click="downloadRouteDataExcel('route')"
            >
              {{ "download excel file to update bonus" }}
            </v-btn>
            <v-btn
              v-else
              class="primary"
              depressed
              @click="downloadSampleExcel()"
            >
              {{ "download sample excel file" }}
            </v-btn>
          </v-col>

          <v-col cols="12" class="d-flex justify-space-between pt-0">
            <span class="text-capitalize">
              {{ "total records" }} : {{ totalRecordCount }}
            </span>
            <span> {{ "records with error" }} : {{ rowData.length }}</span>
          </v-col>

          <v-col v-if="distinctErrors.length > 0" class="py-0">
            <v-alert border="left" colored-border color="red">
              <v-row no-gutters>
                <v-col class="grow"></v-col>
                <v-col class="shrink">
                  <v-btn
                    class="error"
                    depressed
                    @click="removeAllRecordWithError"
                  >
                    {{ "remove all with error" }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>

          <v-col cols="12" v-if="rowData.length > 0">
            <AgGridVue
              @grid-ready="gridReady"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :default-col-def="defaultColDef"
              :context="context"
              :row-data="rowData"
              style="width: 100%; height: 400px"
              class="ag-theme-material"
            >
            </AgGridVue>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4 d-flex justify-end">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!file || rowData.length != 0 || loading"
          @click="submitData()"
        >
          {{ "submit" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <Instruction
      v-model="instructionDialog"
      :instructionList="instructionList"
    />
  </v-dialog>
</template>

<script>
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import { read, utils } from "xlsx";
import {
  customerTariffInstruction,
  locationInstruction,
  driverInstruction,
  vehicleInstruction,
  vehicleCostInstruction,
  trailerInstruction,
  transporterCompanyInstruction,
  companyInstruction,
  leaseDriverBonusInstruction,
  subHireCommitmentInstruction,
  bulkDAInstruction,
  tripInstruction,
} from "@/utils/instruction.js";
import { AgGridVue } from "ag-grid-vue";
import Instruction from "@/components/General/Instruction.vue";
import RemoveRowButtonBulkUpload from "@/components/General/RemoveRowButtonBulkUpload.vue";
import { downloadBlobData } from "@/utils/functions.js";
import BulkUploadErrorTooltip from "@/components/General/BulkUploadErrorTooltip.vue";

export default {
  components: {
    AgGridVue,
    Instruction,
    RemoveRowButtonBulkUpload,
    BulkUploadErrorTooltip,
    MultiSelect,
  },

  props: {
    value: Boolean,
    uploadTo: {
      required: true,
      type: String,
    },
    extraQueryParams: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      selectedCustomer: null,
      customerList: [],
      customerLoading: false,
      loading: false,
      file: null,
      fulldata: [],
      rowData: [],
      columnDefs: [],
      _reqFields: [],
      _allFields: null,
      gridApi: null,
      columnApi: null,
      gridOptions: {
        headerHeight: 40,
        rowHeight: 40,
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
      },
      defaultColDef: {
        lockPosition: true,
      },
      requiredHeaders: [],
      serverErrors: [],

      // Instructions dialog
      instructionList: [],
      instructionDialog: false,
      dataObject: [],
      excelFile: null,
    };
  },

  watch: {
    vehicleBulkUploadDialog(val) {
      if (val && this.uploadTo === 'bulk_da_upload') {
        this.fetchCustomers();
      }
    },
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
    totalRecordCount() {
      return this.dataObject.filter((item) => Object.keys(item).length > 0)
        .length;
    },
    vehicleBulkUploadDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    distinctErrors() {
      let err = [];
      this.serverErrors
        .filter((e) => Object.keys(e).length > 0)
        .map((m) => Object.keys(m).map((k) => m[k]))
        .forEach((r) => {
          r.forEach((s) => {
            const values = Array.isArray(s) ? s : [s];
            err = [...new Set([...err, ...values])];
          });
        });
      return err;
    },
  },

  methods: {
    fetchCustomers() {
      this.customerLoading = true;
      this.$api.company
        .getCompanySelectList({
          company_type: "Customer",
          payment_method: "Credit",
          limit: 500,
        })
        .then((res) => {
          this.customerList = res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.customerLoading = false;
        });
    },

    // ── Label for dialog title ────────────────────────────────────────────
    getTextChange(type) {
      const map = {
        vehicle: "bulk upload vehicle",
        bulk_gps_profile: "bulk upload gps profile",
        customer_tariff: "bulk upload customer tariff",
        trailer: "bulk upload trailer",
        driver: "bulk upload driver",
        vehicle_cost: "bulk upload vehicle cost",
        location: "bulk upload location",
        bulk_da_upload: "bulk upload DA",
        company: "bulk upload customer",
        transporterCompany: "bulk upload transporter",
        route: "update bonus",
        driver_bonus: "bulk upload driver bonus",
        vendor_contract_rate: "bulk upload vendor contract rates",
        sub_hire_commitment: "bulk upload sub hire commitment",
        trip: "bulk upload trip",
      };
      return map[type] || type;
    },

    // ── Instructions dialog ───────────────────────────────────────────────
    openInstructionDialog() {
      const map = {
        customer_tariff: customerTariffInstruction,
        customer_tariff_update: customerTariffInstruction,
        location: locationInstruction,
        location_update: locationInstruction,
        driver: driverInstruction,
        vehicle: vehicleInstruction,
        vehicle_cost: vehicleCostInstruction,
        trailer: trailerInstruction,
        company: companyInstruction,
        bulk_da_upload: bulkDAInstruction,
        transporterCompany: transporterCompanyInstruction,
        driverBonus: transporterCompanyInstruction,
        driver_bonus: leaseDriverBonusInstruction,
        sub_hire_commitment: subHireCommitmentInstruction,
        trip: tripInstruction,
      };
      this.instructionList = map[this.uploadTo] || [];
      this.instructionDialog = true;
    },

    gridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },

    // Excel can render a date cell with a 2-digit year (e.g. "09/17/26")
    // depending on the cell's display format, which the backend's
    // DateField input_formats can't parse (they all expect a 4-digit
    // year). Expand a trailing 2-digit year using the same windowing
    // Excel itself uses: 00-68 -> 2000-2068, 69-99 -> 1969-1999.
    normalizeIfDate(value) {
      if (typeof value !== "string") return value;
      const match = value.trim().match(/^(\d{1,2}[/-]\d{1,2}[/-])(\d{2})$/);
      if (!match) return value;
      const [, prefix, yy] = match;
      const year = Number(yy);
      const fullYear = year <= 68 ? 2000 + year : 1900 + year;
      return `${prefix}${fullYear}`;
    },

    getFileData(file) {
      if (!file) {
        this.clearDialogData();
        return;
      }

      this.excelFile = file;

      this.loading = true;
      this.$bus.$emit("showLoader", true);

      const reader = new FileReader();

      reader.onload = (e) => {
      
        setTimeout(() => {
          try {
            const wb = read(e.target.result, {
              type: "array",
              cellDates: false,
              raw: false,
              dateNF: "yyyy-mm-dd",
            });

            const rowData = utils.sheet_to_row_object_array(
              wb.Sheets[wb.SheetNames[0]],
              {
                header: 0,
                defval: "",
                raw: false,
                dateNF: "yyyy-mm-dd",
              }
            );

            this.dataObject = rowData
              .filter((row) => Object.values(row).some((v) => v !== ""))
              .map((row, index) => {
                const newObject = { oldIndex: index };
                for (const [key, value] of Object.entries(row)) {
                  const newKey = key
                    .toString()
                    .toLowerCase()
                    .trim()
                    .replaceAll(" ", "_");
                  newObject[newKey] = this.normalizeIfDate(value);
                }
                return newObject;
              });
          } finally {
            // Always hide loader and re-enable submit
            // whether parse succeeded or threw
            this.loading = false;
            this.$bus.$emit("showLoader", false);
          }
        }, 0);
      };

      reader.readAsArrayBuffer(file);
    },

    // ── Row removal ───────────────────────────────────────────────────────
    removeErrorRow(index, oldIndex) {
      setTimeout(() => {
        this.serverErrors.splice(index, 1);
        this.rowData.splice(index, 1);
        this.dataObject.splice(
          this.dataObject.findIndex((item) => item.oldIndex == oldIndex),
          1
        );
      }, 200);
    },

    dataChange(param) {
      let field = param.colDef.field;
      let localError = this.serverErrors[param.node.rowIndex];
      if (param && param.newValue != param.oldValue) {
        delete localError[field];
      }
      let newIndex = this.dataObject.findIndex(
        (item) => item.oldIndex == param.data.oldIndex
      );
      this.dataObject[newIndex] = param.data;

      if (
        (localError && localError.non_field_errors) ||
        (localError &&
          Object.keys(localError) &&
          Object.keys(localError).length == 0)
      ) {
        setTimeout(() => {
          this.serverErrors.splice(param.node.rowIndex, 1);
          this.rowData.splice(param.node.rowIndex, 1);
        }, 200);
      }
      return param.newValue;
    },

    cellStyle(param) {
      if (
        param &&
        param.colDef &&
        param.colDef.field &&
        this.serverErrors &&
        this.serverErrors[param.rowIndex] &&
        this.serverErrors[param.rowIndex][param.colDef.field]
      ) {
        return { backgroundColor: "red" };
      }
      return { backgroundColor: "white" };
    },

    removeAllRecordWithError() {
      while (this.rowData && this.rowData.length > 0) {
        this.removeDataFromRow(this.rowData[0].oldIndex, 0);
      }
    },

    removeDataFromRow(oldIndex, currentIndex) {
      this.dataObject.splice(oldIndex, 1, {});
      this.rowData.splice(currentIndex, 1);

      const data = this.dataObject.filter(
        (obj) => Object.keys(obj).length !== 0
      );
      if (data.length == 0) {
        this.clearDialogData();
      }
      if (this.rowData.length == 0) {
        this.serverErrors = [];
      }
    },

    // ── Sample Excel download ─────────────────────────────────────────────
    downloadSampleExcel() {
      const urlMap = {
        vehicle: "bulk_vehicle",
        vehicle_update: "bulk_vehicle",
        vehicle_cost: "bulk_vehicle_cost",
        trailer: "bulk_trailer",
        trailer_update: "bulk_trailer",
        customer_tariff: "bulk_customer_contract",
        customer_tariff_update: "bulk_customer_contract",
        driver: "bulk_driver",
        driver_update: "bulk_driver",
        location: "bulk_location",
        location_update: "bulk_location",
        company: "bulk_company",
        company_update: "bulk_company",
        bulk_da_upload: "bulk_da_upload",
        transporterCompany: "bulk_transporter_company",
        transporterCompany_update: "bulk_transporter_company",
        driver_bonus: "bulk_driver_bonus",
        driver_bonus_update: "bulk_driver_bonus",
        sub_hire_commitment: "bulk_sub_hire_commitment",
        trip: "bulk_trip",
        lrfq_rates: "bulk_lrfq_rates_update",
        lrfq_rates_update: "bulk_lrfq_rates_update",
        bulk_gps_profile: "bulk_gps_profile",
        vendor_contract_rate: "bulk_vendor_contract_rate",
      };

      const newUrl = urlMap[this.uploadTo] || this.uploadTo;
      const params = { excel_type: newUrl };
      if (this.uploadTo === 'bulk_da_upload' && this.selectedCustomer) {
        params.customer = this.selectedCustomer;
      }

      this.$api.bulkUpload
        .downloadSampleFile(params)
        .then((res) => {
          downloadBlobData(res, `Sample ${this.uploadTo} excel file.xls`);
          this.$bus.$emit("showToastMessage", {
            message: "Download file successfully",
            color: "success",
          });
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Can't download file!",
            color: "error",
          });
        });
    },

    async downloadRouteDataExcel(downloadModule) {
      const params = { download_type: downloadModule };
      this.$bus.$emit("showLoader", true);
      await this.$api.lane
        .downloadRouteExcel(params)
        .then(async (response) => {
          this.$bus.$emit("showLoader", false);
          const blob = response.data;
          const downloadLink = window.document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(
            new Blob([blob], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            })
          );
          downloadLink.download = downloadModule + ".xlsx";
          downloadLink.click();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Data not found"
            }`,
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },

    // ── Upload URL map ────────────────────────────────────────────────────
    getUploadUrl(type) {
      const map = {
        lane_rates: "/api/v1/lane_rate/bulk_create/",
        lane_rates_update: "/api/v1/lane_rate/bulk_update/",
        vehicle: "/api/v1/vehicle/bulk_create/",
        vehicle_cost: "/api/v1/vehicle_cost/bulk_create/",
        vehicle_update: "/api/v1/vehicle/bulk_update/",
        customer_tariff: "/api/v1/bulk_customer_contract/",
        vendor_contract_rate: "/api/v1/transporter_contract_rate/bulk_create/",
        trailer: "/api/v1/trailer/bulk_create/",
        trailer_update: "/api/v1/trailer/bulk_update/",
        driver: "/api/v1/driver/bulk_create/",
        driver_bonus: "/api/v1/bulk_upload/",
        sub_hire_commitment: "/api/v1/bulk_upload/",
        trip: "/api/v1/bulk_upload/",
        driver_update: "/api/v1/driver/bulk_update/",
        transporter_lrfq: "/api/v1/transporter_lrfq/bulk_update/",
        location: "/api/v1/location/bulk_create/",
        location_update: "/api/v1/location/bulk_update/",
        company: "/api/v1/company/bulk_create/",
        bulk_da_upload: "/api/v1/da/bulk_create/",
        transporterCompany: "/api/v1/company/bulk_create/",
        company_update: "/api/v1/company/bulk_update/",
        route: "/api/v1/bulk_upload/",
        bulk_gps_profile: "/api/v1/gps_profile/bulk_create/",
      };
      return map[type];
    },

    async uploadDriverBonusDocuments(url, id) {
      let newForm = new FormData();
      newForm.append("document", this.excelFile);
      newForm.append("lease_upload_id", id);
      await this.$api.bulkUpload.bulkUploadFile(url, newForm);
    },

    // ── Submit ────────────────────────────────────────────────────────────
    submitData(payload = {}) {
      this.serverErrors = [];
      let url = this.getUploadUrl(this.uploadTo);
      if (this.extraQueryParams && Object.keys(this.extraQueryParams).length) {
        const qs = new URLSearchParams(this.extraQueryParams).toString();
        url = `${url}?${qs}`;
      }

      if (!this.dataObject || this.dataObject.length === 0) {
        this.$bus.$emit("showToastMessage", {
          message: "Data not found!",
          color: "error",
        });
        return;
      }

      if (this.uploadTo == "route") {
        payload.upload_type = "bulk_route";
        payload.bulk_data = this.dataObject.filter(
          (item) => Object.keys(item).length > 0
        );
      } else if (this.uploadTo == "driver_bonus") {
        payload.upload_type = "bulk_driver_bonus";
        payload.bulk_data = this.dataObject.filter(
          (item) => Object.keys(item).length > 0
        );
      } else if (this.uploadTo == "sub_hire_commitment") {
        payload.upload_type = "bulk_sub_hire_commitment";
        payload.bulk_data = this.dataObject.filter(
          (item) => Object.keys(item).length > 0
        );
      } else if (this.uploadTo == "trip") {
        payload.upload_type = "bulk_trip";
        payload.bulk_data = this.dataObject.filter(
          (item) => Object.keys(item).length > 0
        );
      } else {
        payload = this.dataObject.filter(
          (item) => Object.keys(item).length > 0
        );
        if (this.uploadTo == "company") {
          payload = { data: [...payload], company_type: "Customer" };
        } else if (
          this.uploadTo == "transporterCompany" ||
          this.uploadTo == "lrfq_rates_update"
        ) {
          payload = { data: [...payload], company_type: "Transporter" };
        } else if (this.uploadTo == "vendor_contract_rate") {
          payload = { data: [...payload] };
        }
      }

      this.loading = true;
      this.$bus.$emit("showLoader", true);

      this.$api.bulkUpload
        .bulkUploadFile(url, payload)
        .then((resp) => {
          if (this.uploadTo == "driver_bonus") {
            this.uploadDriverBonusDocuments(url, resp.data.id);
          }
          this.$bus.$emit("showLoader", false);
          this.$emit("refreshList");
          this.loading = false;
          this.$bus.$emit("showToastMessage", {
            message: "Successfully uploaded file",
            color: "success",
          });
          this.serverErrors = [];
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          if (typeof err == "string") {
            this.$bus.$emit("showToastMessage", {
              message: err,
              color: "error",
            });
          } else if (err && typeof err == "object") {
            this.serverErrors = err;
            this.generateErrorRow(err);
            this.$bus.$emit("showToastMessage", {
              message: "Error uploading sheet!",
              color: "error",
            });
          }
          this.loading = false;
        });
    },

    // ── Error grid ────────────────────────────────────────────────────────
    generateErrorRow(errorRow) {
      this.columnDefs = Object.keys(this.dataObject[0]).map((item) => ({
        headerName: item.replaceAll("_", " ").toUpperCase(),
        field: item,
        editable: item !== "oldIndex",
        hide: item === "oldIndex" || item === "formError",
        valueParser: this.dataChange,
        cellStyle: this.cellStyle,
      }));

      this.columnDefs.unshift({
        pinned: "left",
        headerName: "errors",
        width: 90,
        cellRendererFramework: "BulkUploadErrorTooltip",
      });

      this.columnDefs.push({
        headerName: "actions",
        pinned: "right",
        width: 90,
        cellRendererFramework: "RemoveRowButtonBulkUpload",
      });


      const sentRows = this.dataObject.filter(
        (item) => Object.keys(item).length > 0
      );

      const errorsWithRow = errorRow.filter(
        (item) => Object.keys(item).length > 0
      );

      // Some validators (trip, sub hire commitment) only return one entry
      // per ERRORING row and identify it via a 1-based sheet row number
      // (data index + 2, accounting for the header row) in "row"/"Row" -
      // map back to the matching sentRows entry using that. Others (route,
      // driver bonus, fuel surcharge) return one entry per SHEET row
      // (including empty objects for valid rows), already aligned with
      // sentRows by position - keep using the array index for those.
      this.rowData = errorsWithRow
        .map((formError, idx) => {
          const sheetRow = formError.row ?? formError.Row;
          const row =
            sheetRow != null ? sentRows[Number(sheetRow) - 2] : sentRows[idx];
          return row ? { ...row, formError } : null;
        })
        .filter((row) => row !== null);

      this.serverErrors = this.rowData.map((row) => row.formError);
    },

    // ── Dialog close / clear ──────────────────────────────────────────────
    closeDialog() {
      this.vehicleBulkUploadDialog = false;
      this.clearDialogData();
    },

    clearDialogData() {
      this.file = null;
      this.fulldata = [];
      this.rowData = [];
      this.columnDefs = [];
      this.dataObject = [];
      this._reqFields = [];
      this.serverErrors = [];
      this.loading = false;
      this.selectedCustomer = null;
    },
  },
};
</script>